/**
 * World Map window content (lane AK, plan 22) — registry id `worldMap`, opened
 * with the `world_map` binding (M by default since v2.6) or the quick menu. Builds the map
 * once from the static level registry and zone content, and follows the
 * player's current map through the sticky LEVEL_LOADED event (replayed on
 * subscribe, so opening the window mid-session still knows where you are).
 * Prototype: no fast travel. Pocket maps this character has discovered
 * (server-owned `knownPlaces`) show only as notes on their parent. Fog of war
 * is off unless a reason turns it on (worldMapFogPolicy.ts); it then charts the
 * places in the same `knownPlaces` (worldMapFog.ts).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { EventBus, EVENTS } from '../../../../shared/EventBus';
import { getZoneConfig } from '../../../../shared/content/generated/zones.gen';
import { LevelRegistry } from '../../../systems/map/LevelRegistry';
import { WORLD_MAP_LAYOUT } from './worldMapLayout';
import { buildWorldMap } from './worldMapModel';
import { WatercolorWorldMap } from './WatercolorWorldMap';
import { projectKnownPlaces } from './worldMapKnownPlaces';
import { useOptionalGame } from '../../contexts/GameContext';
import { chartWorldMap } from './worldMapFog';
import { frontierRoads } from './worldMapFrontier';
import { readDevFogOverride, resolveWorldMapFog } from './worldMapFogPolicy';
import { useAtlasFocusRequest } from './atlasFocus';

/** The live GM event's template id, if one is running in this room. */
function liveEventTemplateId(): string | null {
  const event = (globalThis as { nm?: { getRoom?: () => { state?: { gmEvent?: { phase?: string; templateId?: string } } } | null } })
    .nm?.getRoom?.()?.state?.gmEvent;
  return event?.phase === 'live' && event.templateId ? event.templateId : null;
}

/** The id of the map the player is on, from the sticky LEVEL_LOADED event. */
export function useCurrentMapId(): string | null {
  const [mapId, setMapId] = useState<string | null>(null);
  useEffect(() => EventBus.getInstance().on(EVENTS.LEVEL_LOADED, (data: { id?: string }) => {
    if (data?.id) setMapId(data.id);
  }), []);
  return mapId;
}

export const WorldMapContent: React.FC = () => {
  const currentMapId = useCurrentMapId();
  const player = useOptionalGame()?.player ?? null;
  const knownPlaceIds = player?.knownPlaces;
  const model = useMemo(() => {
    const registry = LevelRegistry.getInstance();
    return buildWorldMap(WORLD_MAP_LAYOUT, id => registry.getLevel(id), getZoneConfig);
  }, []);
  const knownPlaces = useMemo(() => {
    const registry = LevelRegistry.getInstance();
    return projectKnownPlaces(model, knownPlaceIds, id => registry.getLevel(id));
  }, [model, knownPlaceIds]);
  const fog = useMemo(() => {
    const { policy } = resolveWorldMapFog({ eventTemplateId: liveEventTemplateId(), devOverride: readDevFogOverride() });
    return chartWorldMap(model, policy, { currentMapId, knownPlaceIds });
  }, [model, currentMapId, knownPlaceIds]);
  const frontier = useMemo(() => {
    const registry = LevelRegistry.getInstance();
    return frontierRoads(model.places.map(place => place.id), id => registry.getLevel(id), getZoneConfig);
  }, [model]);
  const focusRequest = useAtlasFocusRequest();
  return <WatercolorWorldMap model={model} currentMapId={currentMapId} knownPlaces={knownPlaces} fog={fog} frontier={frontier} focusRequest={focusRequest} player={player} />;
};
